import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EventFormModal from './EventFormModal'; // Add this at the top
import moment from 'moment';
import PropTypes from 'prop-types';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import MobileNavigation from './components/MobileNavigation';
import MobileFooter from './components/MobileFooter';
import SeriesModal from './components/SeriesModal';
import CustomDateRangeModal from './components/CustomDateRangeModal';
import ListView from './components/ListView';
import ThreeDayView from './components/ThreeDayView';
import {
  Calendar,
  DateLocalizer,
  momentLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useRef, useLayoutEffect } from 'react';
import './App.css';
import { API } from './api/api';
import { formatHours, transformTimelogsToEvents, localDateKey } from './utils/events';
import { createCalendarComponents } from './components/calendar/createCalendarComponents';
import { useContextMenus } from './hooks/useContextMenus';
import { useCalendarMetrics } from './hooks/useCalendarMetrics';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import { useBuildVersionCheck } from './hooks/useBuildVersionCheck';
import { useTimelogEventStream } from './hooks/useTimelogEventStream';
import { mergeWeekIntoEvents, upsertLogInEvents, removeLogFromEvents, weekStartOf } from './utils/liveTimelogs';
import AuthenticatingModal from './components/AuthenticatingModal';
import WeeklyTimeLogModal from './components/WeeklyTimeLogModal';
import { useTheme } from './hooks/useTheme';


// Floor on how often a tab re-reads the delegated-access list when it regains
// focus. Long enough that alt-tabbing costs nothing, short enough that a grant
// changed in the admin dashboard lands within a minute of switching back.
const ACCESS_SYNC_MIN_INTERVAL = 60 * 1000;

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

moment.locale('en', {
  week: {
    dow: 1, // Start week on Monday
  },
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
  }
});

const FORMATS = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),

    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,

    selectRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} – ${localizer.format(end, 'HH:mm', culture)}`,

    agendaTimeFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),
  };

function App() {
  const { isDark, toggleTheme } = useTheme();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [timelogEmployees, setTimelogEmployees] = useState([]);
  const [selectedTimelogEmployee, setSelectedTimelogEmployee] = useState(null);
  const [timelogEmployeeLoading, setTimelogEmployeeLoading] = useState(false);
  const [devLoginEnabled, setDevLoginEnabled] = useState(false);
  const [popupLoginBlocked, setPopupLoginBlocked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const [showOutlookSync, setShowOutlookSync] = useState(true);
  const [defaultUseNotes, setDefaultUseNotes] = useState(() => {
    return localStorage.getItem('zrm_default_use_notes') === 'true';
  });

  // Identify Outlook sync events: prefer the explicit `outlookSync` flag,
  // falling back to the legacy timecode-name check only when it's undefined.
  const isOutlookSyncEvent = useCallback((event) => {
    return event.outlookSync !== undefined ? event.outlookSync : event.timecodeName === '(SYNC) Outlook Sync';
  }, []);

  const filteredEvents = useMemo(() => {
    return events.filter(ev => showOutlookSync || !isOutlookSyncEvent(ev));
  }, [events, showOutlookSync, isOutlookSyncEvent]);

  const getModalPositionStyle = useCallback((date, view) => {
    const day = moment(date).day();
    let positionStyle = { right: '20px' };
    if (view === 'week') {
      if (day === 0 || day >= 4) positionStyle = { left: '20px' };
    } else if (view === 'work_week') {
      if (day >= 3) positionStyle = { left: '20px' };
    } else if (view === 'three_day') {
      const eventDate = moment(date).startOf('day');
      let rangeStart = currentDate ? moment(currentDate).startOf('day') : eventDate;
      const dayIndex = eventDate.diff(rangeStart, 'days');
      if (dayIndex === 2 || dayIndex < 0 || dayIndex > 2) {
        positionStyle = { left: '20px' };
      }
    }
    return {
      ...positionStyle,
      position: 'fixed',
      top: '80px',
      zIndex: 2600,
    };
  }, [currentDate]);

  const [modalPosition, setModalPosition] = useState(() => getModalPositionStyle(new Date(), currentView));
  const [isDragging, setIsDragging] = useState(false);

  // Show error in modal instead of taking over the page
  const showError = useCallback((title, message) => {
    setErrorModalData({ title, message });
    setShowErrorModal(true);
  }, []);

  const {
    eventToCopy,
    setEventToCopy,
    contextMenu,
    setContextMenu,
    contextMenuRef,
    slotContextMenu,
    setSlotContextMenu,
    slotContextMenuRef,
    handleRightClickEvent,
    handleRightClickSlot,
  } = useContextMenus();
  const [originalEventBeforeEdit, setOriginalEventBeforeEdit] = useState(null);
  const suppressRevertRef = useRef(false); // <-- add this

  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  
  // Series Modal state
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesTemplateEvent, setSeriesTemplateEvent] = useState(null);
  
  // Series Delete state
  const [showSeriesDeleteConfirm, setShowSeriesDeleteConfirm] = useState(false);
  const [seriesPendingDelete, setSeriesPendingDelete] = useState({ seriesId: null, events: [] });
  const [seriesDeleteProgress, setSeriesDeleteProgress] = useState(null); // null = not deleting, { current, total } = in progress

  // Custom Date Range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [customRangeData, setCustomRangeData] = useState(null);
  const [isLoadingCustomRange, setIsLoadingCustomRange] = useState(false);

  const [billableAutoFill, setBillableAutoFill] = useState(false);

  // Weekly Time Log
  const [showWeeklyLog, setShowWeeklyLog] = useState(false);

  // Outlook Sync Removal state
  const [showRemoveSyncsConfirm, setShowRemoveSyncsConfirm] = useState(false);
  const [outlookSyncsToRemove, setOutlookSyncsToRemove] = useState([]);
  const [showRemoveSyncsResult, setShowRemoveSyncsResult] = useState(false);

  const canWriteTimelogs = selectedTimelogEmployee?.accessLevel !== 'read';

  // syncTimelogAccess runs from a focus listener, so it reads the selection
  // through a ref rather than closing over a value captured at register time.
  const selectedTimelogEmployeeRef = useRef(null);
  useEffect(() => { selectedTimelogEmployeeRef.current = selectedTimelogEmployee; }, [selectedTimelogEmployee]);
  // Seeded at mount, which already fetched the list fresh — so the first focus
  // after loading doesn't immediately fetch it again.
  const lastAccessSyncRef = useRef(Date.now());



  // Handle Create Series functionality (from right-click)
  const handleCreateSeries = useCallback((templateEvent) => {
    setSeriesTemplateEvent(templateEvent);
    setShowSeriesModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
  }, []);

  // Handle Create Series from Header (without template)
  const handleCreateSeriesFromHeader = useCallback(() => {
    // Create a default template event for new series
    const defaultTemplate = {
      title: '',
      projectId: '',
      timecodeId: '',
      billingtype: 'Billable',
      projectName: '',
      timecodeName: '',
      approval_status: 'Pending',
      start: new Date(),
      end: new Date(),
    };
    setSeriesTemplateEvent(defaultTemplate);
    setShowSeriesModal(true);
  }, []);

  // Handle Custom Date Range
  const handleCustomDateRangeClick = useCallback(() => {
    setShowCustomDateModal(true);
  }, []);

  const handleCustomDateRangeClose = useCallback(() => {
    setShowCustomDateModal(false);
  }, []);

  const handleClearCustomRange = useCallback(() => {
    setCustomDateRange(null);
    setCustomRangeData(null);
  }, []);

  // Helper function for long press on mobile
  const setupLongPress = useCallback((handler, contextData = null) => ({
    onTouchStart: (e) => {
      const touchTimer = setTimeout(() => {
        const syntheticEvent = {
          preventDefault: () => e.preventDefault(),
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          target: e.target,
          currentTarget: e.currentTarget
        };
        handler(contextData || e, syntheticEvent);
      }, 500); // 500ms long press
      
      e.currentTarget._touchTimer = touchTimer;
    },
    onTouchEnd: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    },
    onTouchMove: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    }
  }), []);

  // Handle Delete Entire Series functionality
  const handleDeleteSeries = useCallback((seriesId) => {
    const seriesEvents = events.filter(event => event.seriesId === seriesId);
    if (seriesEvents.length > 0) {
      setSeriesPendingDelete({ seriesId, events: seriesEvents });
      setShowSeriesDeleteConfirm(true);
      setContextMenu({ visible: false, x: 0, y: 0, event: null });
    }
  }, [events]);

  // Confirm and execute series deletion
  const handleConfirmDeleteSeries = useCallback(async () => {
    setShowSeriesDeleteConfirm(false);
    const eventsWithLogs = seriesPendingDelete.events.filter(e => e.logId);

    // Group events by ISO week — one bulk call per week (max 90 weeks)
    const weekMap = new Map();
    for (const e of eventsWithLogs) {
      const d = new Date(e.start);
      const day = d.getDay();
      const monday = new Date(d);
      monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      const key = monday.toISOString().split('T')[0];
      if (!weekMap.has(key)) weekMap.set(key, []);
      weekMap.get(key).push(e);
    }
    const batches = [...weekMap.values()];
    if (batches.length > 90) {
      showError('Delete Failed', `Series spans ${batches.length} weeks (max 90).`);
      return;
    }

    setSeriesDeleteProgress({ current: 0, total: batches.length });
    try {
      for (let i = 0; i < batches.length; i++) {
        await API.deleteTimelogsBulk({
          logs: batches[i].map(e => ({ logId: e.logId })),
        });
        setSeriesDeleteProgress({ current: i + 1, total: batches.length });
      }
      setEvents(prev => prev.filter(event => event.seriesId !== seriesPendingDelete.seriesId));
      setSeriesPendingDelete({ seriesId: null, events: [] });
    } catch (error) {
      console.error('Error deleting series:', error);
      showError('Delete Failed', 'Failed to delete some events in the series. Please try again.');
    } finally {
      setSeriesDeleteProgress(null);
    }
  }, [seriesPendingDelete, showError]);

  // Handle batch creation of series events
  const handleBatchCreateSeries = useCallback(async (eventsToCreate, setProgress) => {
    const seriesId = eventsToCreate[0]?.seriesId;
    if (!seriesId) throw new Error('Series ID is required');

    // Group events by ISO week — one bulk call per week (max 90 weeks)
    const weekMap = new Map();
    for (const e of eventsToCreate) {
      const d = new Date(e.start);
      const jan4 = new Date(d.getFullYear(), 0, 4);
      const isoWeek = Math.ceil(((d - jan4) / 86400000 + jan4.getDay() + 1) / 7);
      const key = `${d.getFullYear()}-${isoWeek}`;
      if (!weekMap.has(key)) weekMap.set(key, []);
      weekMap.get(key).push(e);
    }
    const batches = [...weekMap.values()];
    if (batches.length > 90) throw new Error(`Series too large — spans ${batches.length} weeks (max 90)`);

    try {
      setProgress({ current: 0, total: eventsToCreate.length });
      let completed = 0;
      let totalFailures = 0;

      for (const batch of batches) {
        const data = await API.createTimelogsBulk({
          events: batch.map(e => ({
            matterId: e.projectId,
            workTypeId: e.timecodeId,
            notes: e.title,
            billStatus: e.billingtype,
            date: localDateKey(e.start),
            start: e.start,
            end: e.end,
            sendToCapLegal: e.sendToCapLegal,
          })),
        });

        // Response items already contain everything needed to build an event.
        // Correlate them back to the originating batch entries (for seriesId)
        // by skipping any indexes reported in `failures`.
        const failedIndexes = new Set((data?.failures || []).map(f => f.index));
        const succeededBatchEntries = batch.filter((_, i) => !failedIndexes.has(i));
        const createdEvents = transformTimelogsToEvents(data?.data || []);
        createdEvents.forEach((createdEvent, i) => {
          setEvents(prev => [...prev, {
            ...createdEvent,
            seriesId: succeededBatchEntries[i]?.seriesId ?? seriesId,
          }]);
        });

        if (data?.failures?.length) totalFailures += data.failures.length;

        completed += batch.length;
        setProgress({ current: completed, total: eventsToCreate.length });
      }
      if (authenticated) {
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});
      }
      if (totalFailures > 0) {
        showError('Partial Failure', `${totalFailures} of ${eventsToCreate.length} entries failed to save.`);
      }
    } catch (error) {
      console.error('🚨 Error creating series:', error);
      throw error;
    }
  }, [authenticated, showError]);

  const createMarkedPlaceholder = useCallback((start, end, allDay) => {
    const tempId = `temp-${Date.now()}`;
    const base = {
      title: '',
      start,
      end,
      logId: tempId,
      approval_status: 'Marked',
      projectId: '',
      timecodeId: '',
      billingtype: '',
      projectName: '',
      timecodeName: '',
    };
    return allDay === undefined ? base : { ...base, allDay };
  }, []);

  // Handle Add New functionality
  const handleAddNew = useCallback(() => {
    // 1. Round up to next 30-minute interval (hel/halv timer)
    const now = (currentView === 'day' || currentView === 'three_day') ? new Date(currentDate) : new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    } else {
      now.setMinutes(roundedMinutes, 0, 0);
    }
    const start = new Date(now);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

    const placeholder = createMarkedPlaceholder(start, end);
    setEvents(prev => [
      ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
      placeholder
    ]);
    setSlotInfo({ start, end, tempId: placeholder.logId });
    setModalPosition(getModalPositionStyle(start, currentView));
    setShowModal(true);
  }, [currentView, currentDate, setEvents, setSlotInfo, setModalPosition, setShowModal, getModalPositionStyle, createMarkedPlaceholder]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if not typing in an input or textarea and modal is not open
      if (
        e.key === 'n' &&
        canWriteTimelogs &&
        !showModal &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        handleAddNew();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, handleAddNew, canWriteTimelogs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
      
      if (
        slotContextMenuRef.current &&
        !slotContextMenuRef.current.contains(event.target)
      ) {
        setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
      }
    };

    if (contextMenu.visible || slotContextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible, slotContextMenu.visible]);

  const isDraggable = useCallback((event) => canWriteTimelogs && event.approval_status !== 'Approved', [canWriteTimelogs]);
  const isResizable = useCallback((event) => canWriteTimelogs && event.approval_status !== 'Approved', [canWriteTimelogs]);

  const calendarRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const previousViewRef = useRef(currentView);
  const previousDateRef = useRef(currentDate);
  const previousAuthRef = useRef(authenticated);

  // Reusable function to scroll calendar to specific time
  const scrollToTime = useCallback((targetTime, smooth = false) => {
    if (!calendarRef.current) return;

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (!scrollContainer) return;

    // Extract hour from target time
    const targetHour = typeof targetTime === 'number' ? targetTime : targetTime.getHours();
    const targetMinutes = typeof targetTime === 'number' ? 0 : targetTime.getMinutes();
    
    // Calculate total minutes from start of day
    const totalMinutes = targetHour * 60 + targetMinutes;
    
    // Try to calculate actual pixels per hour from DOM
    const timeSlots = scrollContainer.querySelectorAll('.rbc-time-slot');
    let pixelsPerHour = 40; // fallback
    
    if (timeSlots.length > 0) {
      const slotHeight = timeSlots[0].offsetHeight;
      const slotsPerHour = 2; // 30-minute intervals = 2 slots per hour
      pixelsPerHour = slotHeight * slotsPerHour;
    }
    
    // Calculate scroll position - add some padding above for context
    const paddingHours = 1; // Show 1 hour above for context
    const scrollPosition = Math.max(0, (totalMinutes / 60 - paddingHours) * pixelsPerHour);
    
    console.log('Scrolling to time:', {
      targetTime: typeof targetTime === 'number' ? `${targetTime}:00` : targetTime.toTimeString(),
      targetHour,
      targetMinutes,
      totalMinutes,
      pixelsPerHour,
      scrollPosition,
      smooth
    });
    
    if (smooth) {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    } else {
      scrollContainer.scrollTop = scrollPosition;
    }
  }, []);

  useLayoutEffect(() => {
    if (!calendarRef.current || showModal) return; // <- skip if modal is open

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');

    if (scrollContainer) {
      // Check if view, date, or auth status has changed
      const viewChanged = previousViewRef.current !== currentView;
      const dateChanged = previousDateRef.current !== currentDate;
      const authChanged = previousAuthRef.current !== authenticated;
      
      // Only auto-scroll on initial load or when view/date/auth changes, not when events are updated
      if (!hasAutoScrolledRef.current || viewChanged || dateChanged || authChanged) {
        // Get current time and scroll to it (with some padding above)
        const now = new Date();
        const currentHour = now.getHours();
        
        // Go back 2 hours for context, but not below 6 AM
        let targetHour = Math.max(6, currentHour - 2);
        
        // If it's early morning (before 8 AM), just start at 6 AM
        if (currentHour < 8) {
          targetHour = 6;
        }
        
        // Debug info
        const reason = !hasAutoScrolledRef.current ? 'initial load' : 
                      (viewChanged ? 'view change' : 
                       (dateChanged ? 'date change' : 'auth change'));
        console.log('Auto-scroll:', { currentHour, targetHour, time: now.toTimeString(), reason });
        
        // Use the reusable scroll function
        scrollToTime(targetHour, false);
        hasAutoScrolledRef.current = true;
      }
      
      // Always update previous values for next comparison (regardless of whether we scrolled)
      previousViewRef.current = currentView;
      previousDateRef.current = currentDate;
      previousAuthRef.current = authenticated;
    }
  }, [currentView, showModal, currentDate, scrollToTime, authenticated]); // Added 'authenticated' to ensure auto-scroll works after login

  // Deliberately outside every auth gate: a device stuck on an old bundle is
  // usually stuck before it ever signs in, so anything gated on `authenticated`
  // never reaches it.
  useBuildVersionCheck();

  useAuthBootstrap({
    authenticated,
    setAuthenticated,
    setProjects,
    setError,
    setBillableAutoFill,
    setCurrentView,
    setDevLoginEnabled,
    setPopupLoginBlocked,
  });

  useEffect(() => {
    if (!authenticated) return;
    setTimelogEmployeeLoading(true);
    Promise.all([
      API.fetchTimelogAccess({ refresh: true }).catch(() => ({ data: [] })),
      API.fetchProjectsTimelogs(),
    ])
      .then(([accessResponse, timelogsResponse]) => {
        const options = accessResponse.data || [];
        const self = options.find(option => option.isSelf) || options[0] || null;
        setTimelogEmployees(options);
        setSelectedTimelogEmployee(self);
        API.setTimelogEmployee(self?.id);
        setEvents(transformTimelogsToEvents(timelogsResponse.data || []));
      })
      // Surface the server's message: "not linked to a CRM Employee record" and
      // a generic fetch failure need different reactions from the user.
      .catch(error => showError('Data Error', error.message || 'Failed to fetch timelogs. Please try refreshing the page.'))
      .finally(() => setTimelogEmployeeLoading(false));
  }, [authenticated, showError]);

  // Delegated access is granted and revoked in a different app (the admin
  // dashboard), which has no way to notify this tab. Without a re-sync the list
  // loaded at startup is frozen for the life of the tab: a new grant never
  // appears, and a revoked one keeps showing an employee every write will now
  // be rejected for. Re-pull it whenever the tab comes back to the foreground.
  const syncTimelogAccess = useCallback(async () => {
    let options;
    try {
      const response = await API.fetchTimelogAccess({ refresh: true });
      options = response.data || [];
    } catch {
      return; // Keep what we have; the next focus (or a reload) resolves it.
    }
    // The signed-in employee is always in the list, so an empty one means the
    // server couldn't resolve the session at all — not a revocation.
    if (options.length === 0) return;
    setTimelogEmployees(options);

    const current = selectedTimelogEmployeeRef.current;
    if (!current) return;

    const match = options.find(option => option.id === current.id);
    if (match) {
      // Still allowed, but the level may have been changed under us.
      if (match.accessLevel !== current.accessLevel) setSelectedTimelogEmployee(match);
      return;
    }

    // A CRM lookup the server couldn't complete degrades to "your own calendar
    // only", which reads exactly like a revocation. Confirm with a second call
    // before yanking someone off the calendar they're working in.
    try {
      const confirmed = (await API.fetchTimelogAccess({ refresh: true })).data || [];
      if (confirmed.some(option => option.id === current.id)) {
        setTimelogEmployees(confirmed);
        return;
      }
    } catch {
      return;
    }

    // The grant backing the calendar on screen is gone — fall back to the
    // signed-in employee rather than leaving someone else's timelogs up.
    const self = options.find(option => option.isSelf) || options[0];
    setShowModal(false);
    setShowWeeklyLog(false);
    setSelectedTimelogEmployee(self);
    API.setTimelogEmployee(self?.id);
    try {
      const response = await API.fetchProjectsTimelogs();
      setEvents(transformTimelogsToEvents(response.data || []));
    } catch {
      // Non-critical — the event stream or the next fetch resyncs.
    }
    showError('Access changed', `Your access to ${current.name}'s timelogs has been removed. Showing your own calendar instead.`);
  }, [showError]);

  useEffect(() => {
    if (!authenticated) return;
    const maybeSync = () => {
      if (document.visibilityState === 'hidden') return;
      // 'focus' and 'visibilitychange' both fire when returning to the tab, and
      // alt-tabbing is frequent — throttle so this stays one CRM read a minute
      // at worst rather than one per switch.
      if (Date.now() - lastAccessSyncRef.current < ACCESS_SYNC_MIN_INTERVAL) return;
      lastAccessSyncRef.current = Date.now();
      syncTimelogAccess();
    };
    window.addEventListener('focus', maybeSync);
    document.addEventListener('visibilitychange', maybeSync);
    return () => {
      window.removeEventListener('focus', maybeSync);
      document.removeEventListener('visibilitychange', maybeSync);
    };
  }, [authenticated, syncTimelogAccess]);

  const handleTimelogEmployeeChange = useCallback(async (employeeId) => {
    const nextEmployee = timelogEmployees.find(employee => employee.id === employeeId);
    if (!nextEmployee || nextEmployee.id === selectedTimelogEmployee?.id) return;

    const previousEmployee = selectedTimelogEmployee;
    setTimelogEmployeeLoading(true);
    setShowModal(false);
    setShowWeeklyLog(false);
    API.setTimelogEmployee(nextEmployee.id);
    try {
      const response = await API.fetchProjectsTimelogs();
      setEvents(transformTimelogsToEvents(response.data || []));
      setSelectedTimelogEmployee(nextEmployee);
    } catch (error) {
      API.setTimelogEmployee(previousEmployee?.id);
      showError('Calendar Error', error.message || 'Failed to load this employee\'s timelogs.');
    } finally {
      setTimelogEmployeeLoading(false);
    }
  }, [timelogEmployees, selectedTimelogEmployee, showError]);

  // Re-pulls the timelog list after a push notification that another viewer
  // (or another tab) changed this employee's calendar. Keeps any in-flight
  // "Marked"/temp placeholder the current user is still editing, since those
  // don't exist server-side yet and would otherwise vanish out from under them.
  const refetchEvents = useCallback(() => {
    API.fetchProjectsTimelogs()
      .then((response) => {
        const fresh = transformTimelogsToEvents(response.data || []);
        setEvents((prev) => [
          ...fresh,
          ...prev.filter(ev => ev.approval_status === 'Marked' || (ev.logId && ev.logId.startsWith('temp-'))),
        ]);
      })
      .catch(() => {
        // Silently fail - not critical, next explicit fetch will resync.
      });
  }, []);

  useTimelogEventStream({
    authenticated,
    employeeId: selectedTimelogEmployee?.id,
    onChange: refetchEvents,
    onUpsert: useCallback((log) => setEvents(prev => upsertLogInEvents(prev, log)), []),
    onRemove: useCallback((id) => setEvents(prev => removeLogFromEvents(prev, id)), []),
  });

  // Stale-while-revalidate for the visible week: the calendar already shows what it
  // loaded (18 month list, up to 15 min old), and here the week on screen is fetched
  // live from CRM and merged over it, on load and on every week change. No spinner:
  // the data on screen stays put until the fresh week replaces it.
  const visibleWeekStart = weekStartOf(currentDate);
  const liveEmployeeId = selectedTimelogEmployee?.id;
  useEffect(() => {
    if (!authenticated || !liveEmployeeId) return;
    let cancelled = false;
    API.fetchTimelogWeek(visibleWeekStart)
      .then((response) => {
        if (cancelled) return;
        setEvents(prev => mergeWeekIntoEvents(prev, response.data || [], visibleWeekStart));
      })
      .catch(() => {
        // Not critical: the cached view stays, and the next navigation retries.
      });
    return () => { cancelled = true; };
  }, [authenticated, liveEmployeeId, visibleWeekStart]);

  // Save user's view preference whenever currentView changes
  useEffect(() => {
    if (authenticated && currentView) {
      API.saveUserViewPreference(currentView).catch(() => {
        // Silently fail - not critical
      });
    }
  }, [currentView, authenticated]);


  const fetchProjects = useCallback(() => {
    API.fetchProjects()
      .then((projectData) => {
        if (projectData.success) {
          setProjects(projectData.data);
        } else {
          showError('Projects Error', 'Failed to fetch projects');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Failed to fetch projects. Please check your connection.');
      });
  }, [showError]);

  const handleConvertCRMSync = useCallback(async (eventData) => {
    const oldLogId = eventData.logId;

    try {
      // First, create the new timelog
      const createData = await API.createTimelog({
        start: eventData.start,
        end: eventData.end,
        matterId: eventData.projectId,
        workTypeId: eventData.timecodeId,
        notes: eventData.title,
        billStatus: eventData.billingtype,
        date: localDateKey(eventData.start),
        sendToCapLegal: eventData.sendToCapLegal,
      });

      if (createData.success) {
        // Delete the old Outlook sync entry
        await API.deleteTimelog({ logId: oldLogId });

        // Update UI: remove old sync event and add new timelog event
        setEvents((prev) => {
          const filtered = prev.filter(ev => ev.logId !== eventData.logId);
          const newEvent = {
            title: eventData.title,
            start: eventData.start,
            end: eventData.end,
            projectId: eventData.projectId,
            timecodeId: eventData.timecodeId,
            projectName: eventData.projectName || '',
            timecodeName: eventData.timecodeName || '',
            billingtype: eventData.billingtype || '',
            approval_status: 'Pending',
            logId: createData.data.id,
          };
          return [...filtered, newEvent];
        });

        fetchProjects(); // Refresh projects list
      } else {
        showError('Conversion Failed', 'Failed to convert Outlook sync to a timelog.');
      }
    } catch (error) {
      console.error('Error converting CRM sync:', error);
      showError('Conversion Error', 'An error occurred while converting the Outlook sync.');
    }
  }, [fetchProjects, showError]);

  const eventPropGetter = useCallback((event) => {
    let backgroundColor, textColor, borderStyle = 'solid', borderColor;

    if (isDark) {
      backgroundColor = '#2d4448';
      borderColor = 'rgba(200, 195, 200, 0.25)';
      textColor = '#e0dee0';
      if (isOutlookSyncEvent(event)) {
        backgroundColor = '#1a3a64';
        borderColor = '#3060a0';
        textColor = '#c8daf8';
      } else if (event.approval_status === 'Approved') {
        backgroundColor = event.billingtype === 'Billable' ? '#243c24' : '#3c3218';
        borderColor = event.billingtype === 'Billable' ? '#406040' : '#604e20';
        textColor = event.billingtype === 'Billable' ? '#c8dcc8' : '#d8cc9a';
      } else if (event.approval_status === 'Rejected' || event.approval_status === 'Changed') {
        backgroundColor = '#403840';
        borderColor = '#605860';
        textColor = '#c8b8c8';
      }
      if (event.approval_status === 'Marked') {
        backgroundColor = 'rgba(204, 183, 120, 0.20)';
        borderStyle = 'dashed';
        borderColor = '#ccb778';
        textColor = '#e8d89a';
      } else if (editingEvent?.logId === event.logId) {
        backgroundColor = '#3a3010';
        borderStyle = 'solid';
        borderColor = '#ccb778';
        textColor = '#e8d890';
      }
    } else {
      backgroundColor = '#a9bec1';
      borderColor = 'rgba(65, 60, 65, 0.4)';
      textColor = 'black';
      if (isOutlookSyncEvent(event)) {
        backgroundColor = '#d0e1f9';
      } else if (event.approval_status === 'Approved') {
        backgroundColor = event.billingtype === 'Billable' ? '#e8eddf' : '#e0dabb';
      } else if (event.approval_status === 'Rejected' || event.approval_status === 'Changed') {
        backgroundColor = '#767279';
        textColor = 'white';
      }
      if (event.approval_status === 'Marked') {
        backgroundColor = 'rgba(204, 183, 120, 0.15)';
        borderStyle = 'dashed';
        borderColor = '#ccb778';
      } else if (editingEvent?.logId === event.logId) {
        backgroundColor = '#fffbe6';
        borderStyle = 'solid';
        borderColor = '#ccb778';
      }
    }

    return {
      style: {
        backgroundColor,
        color: textColor,
        borderRadius: '4px',
        border: `1px ${borderStyle} ${borderColor}`,
        padding: '2px 4px',
        boxShadow: event.approval_status === 'Marked'
          ? '0 0 0 2px #ccb77833'
          : '0 0 10px 0 rgba(0, 0, 0, 0.2)',
        opacity: event.approval_status === 'Marked' ? 0.95 : 1,
      },
    };
  }, [editingEvent, isOutlookSyncEvent, isDark]);

  const {
    totalsCurrentView,
    totalsYTD,
    getDailyTotals,
    getCustomPeriodTotals,
    getYearOverYearComparison,
  } = useCalendarMetrics({ events, currentDate, currentView });

  const handleCustomDateRangeApply = useCallback((range) => {
    setIsLoadingCustomRange(true);
    setCustomDateRange(range);

    try {
      if (range.compareWithPreviousYear) {
        const comparisonData = getYearOverYearComparison(range.start, range.end);
        setCustomRangeData({
          ...comparisonData,
          range,
          label: range.label || 'Custom Range',
        });
      } else {
        const currentPeriod = getCustomPeriodTotals(range.start, range.end);
        setCustomRangeData({
          current: currentPeriod,
          range,
          label: range.label || 'Custom Range',
          hasData: currentPeriod.total > 0,
        });
      }
    } catch (error) {
      console.error('Error calculating custom range data:', error);
      showError('Calculation Error', 'An error occurred while calculating the selected period');
    } finally {
      setIsLoadingCustomRange(false);
    }
  }, [showError, getCustomPeriodTotals, getYearOverYearComparison]);

  const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
    setIsDragging(false);
    setModalPosition(getModalPositionStyle(start, currentView));
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
      if (isMarked) {
        setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
          title: '',
          start,
          end,
          allDay,
          logId: tempId,
          approval_status: 'Marked',
          projectId: '',
          timecodeId: '',
          billingtype: '',
          projectName: '',
          timecodeName: '',
        };
        setEvents(prev => [...prev, placeholder]);
        setSlotInfo({ start, end, tempId });
        setEditingEvent(null);
        setShowModal(true);
        setOriginalEventBeforeEdit(null);
        return;
      }
      if (event.approval_status !== 'Marked') {
        setEditingEvent(event);
        // Remove marked events if selected is not marked
        setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
      }
      // Normal event: open modal for editing
      const updated = { ...event, start, end, allDay, approval_status: 'Changed' };
      setEditingEvent(updated);
      setShowModal(true);
      setEvents((prev) =>
        prev.map((ev) => (ev === event ? updated : ev))
      );
    }, [currentView]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    setIsDragging(false);
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start,
        end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    // Normal event: open modal for editing
    const updated = { ...event, start, end, approval_status: 'Changed' };
    //setOriginalEventBeforeEdit(event);
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) =>
      prev.map((ev) => (ev === event ? updated : ev))
    );
  }, []);

  const handleSaveEvent = useCallback((newEvent) => {
    API.createTimelog({
      start: newEvent.start,
      end: newEvent.end,
      matterId: newEvent.projectId,
      workTypeId: newEvent.timecodeId,
      notes: newEvent.title,
      billStatus: newEvent.billingtype,
      date: localDateKey(newEvent.start),
      sendToCapLegal: newEvent.sendToCapLegal,
      ytdNonBillableHours: newEvent.ytdNonBillableHours ?? 0,
    })
      .then((data) => {
        if (data.success) {
          const newEventWithId = {
            title: newEvent.title,
            start: newEvent.start,
            end: newEvent.end,
            projectId: newEvent.projectId,
            timecodeId: newEvent.timecodeId,
            projectName: newEvent.projectName || '',
            timecodeName: newEvent.timecodeName || '',
            billingtype: newEvent.billingtype || '',
            sendToCapLegal: newEvent.sendToCapLegal,
            approval_status: 'Pending',
            logId: data.data.id,
          };
          setEvents((prev) => [...prev, newEventWithId]);
          fetchProjects();
        } else {
          showError('Save Failed', 'Failed to save event. Please try again.');
        }
      })
      .catch((err) => {
        showError('Save Failed', err.message || 'Server error while saving event. Please check your connection and try again.');
      });
  }, [fetchProjects, showError]);

  const handleUpdateEvent = useCallback((updatedEvent) => {
    API.updateTimelog({
      logId: updatedEvent.logId,
      start: updatedEvent.start,
      end: updatedEvent.end,
      matterId: updatedEvent.projectId,
      workTypeId: updatedEvent.timecodeId,
      notes: updatedEvent.title,
      billStatus: updatedEvent.billingtype,
      date: localDateKey(updatedEvent.start),
      sendToCapLegal: updatedEvent.sendToCapLegal,
      ytdNonBillableHours: updatedEvent.ytdNonBillableHours ?? 0,
    })
      .then((data) => {
        if (data.success) {
          setEvents((prev) =>
            prev.map((ev) =>
              ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev
            )
          );
          setOriginalEventBeforeEdit(null);
        } else {
          showError('Update Failed', 'Failed to update event. Please try again.');
        }
      })
      .catch((err) => {
        // 409 = the record was approved (stamped with a Nav_ID) after this
        // client last saw it. Sync the approved state in so the event locks
        // down instead of just showing a generic error.
        if (err.status === 409 && err.data?.data) {
          const [fresh] = transformTimelogsToEvents([err.data.data]);
          if (fresh) {
            setEditingEvent((prev) => (prev?.logId === updatedEvent.logId ? { ...prev, ...fresh } : prev));
            setEvents((prev) => prev.map((ev) => (ev.logId === updatedEvent.logId ? { ...ev, ...fresh } : ev)));
          }
          setOriginalEventBeforeEdit(null);
          showError('Already Approved', err.message);
          return;
        }
        showError('Update Failed', err.message || 'Server error while updating event. Please check your connection and try again.');
      });
  }, [showError]);

  const handleDeleteEvent = useCallback((logId) => {
    API.deleteTimelog({ logId })
      .then((data) => {
        if (data.success) {
          setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
        } else {
          showError('Delete Failed', 'Failed to delete event. Please try again.');
        }
      })
      .catch((err) => {
        if (err.status === 409 && err.data?.data) {
          const [fresh] = transformTimelogsToEvents([err.data.data]);
          if (fresh) {
            setEditingEvent((prev) => (prev?.logId === logId ? { ...prev, ...fresh } : prev));
            setEvents((prev) => prev.map((ev) => (ev.logId === logId ? { ...ev, ...fresh } : ev)));
          }
          showError('Already Approved', err.message);
          return;
        }
        showError('Connection Error', 'Server error while deleting event. Please check your connection and try again.');
      });
  }, [showError]);

  const handleRemoveOutlookSyncs = useCallback(() => {
    // Helper function to check if event is in current view
    const isInCurrentView = (event) => {
      if (currentView === 'work_week') {
        const weekStart = moment(currentDate).startOf('week').add(0, 'days'); // Monday
        const weekEnd = moment(currentDate).startOf('week').add(4, 'days').endOf('day'); // Friday
        return moment(event.start).isBetween(weekStart, weekEnd, null, '[]');
      }

      const viewStart = moment(currentDate).startOf(
        currentView === 'month'
          ? 'month'
          : currentView === 'week'
          ? 'week'
          : 'day'
      );
      const viewEnd = moment(currentDate).endOf(
        currentView === 'month'
          ? 'month'
          : currentView === 'week'
          ? 'week'
          : 'day'
      );
      return moment(event.start).isBetween(viewStart, viewEnd, null, '[]');
    };

    // Find all Outlook sync events in the current view
    const outlookSyncsInView = events.filter(event => 
      isOutlookSyncEvent(event) && isInCurrentView(event)
    );

    if (outlookSyncsInView.length === 0) {
      showError('No Syncs Found', 'There are no Outlook sync events in the current view.');
      return;
    }

    // Show confirmation modal
    setOutlookSyncsToRemove(outlookSyncsInView);
    setShowRemoveSyncsConfirm(true);
  }, [events, currentDate, currentView, showError, isOutlookSyncEvent]);

  const handleConfirmRemoveSyncs = useCallback(async () => {
    setShowRemoveSyncsConfirm(false);

    // Optimistically remove all events from UI immediately
    const eventsToRemove = outlookSyncsToRemove;
    const logIdsToRemove = new Set(eventsToRemove.map(e => e.logId));
    
    setEvents(prev => prev.filter(ev => !logIdsToRemove.has(ev.logId)));
    setOutlookSyncsToRemove([]);

    // Delete all Outlook sync events in the background
    let successCount = 0;
    let errorCount = 0;
    const failedEvents = [];

    // Process all deletions in parallel
    const deletionPromises = eventsToRemove.map(async (event) => {
      try {
        const response = await API.deleteTimelog({ logId: event.logId });

        if (response.success) {
          successCount++;
          return { success: true, event };
        } else {
          errorCount++;
          failedEvents.push(event);
          return { success: false, event };
        }
      } catch (error) {
        console.error('Error deleting Outlook sync event:', error);
        errorCount++;
        failedEvents.push(event);
        return { success: false, event };
      }
    });

    await Promise.all(deletionPromises);

    // Restore failed deletions back to the UI
    if (failedEvents.length > 0) {
      setEvents(prev => [...prev, ...failedEvents].sort((a, b) => 
        new Date(a.start) - new Date(b.start)
      ));
    }

  }, [outlookSyncsToRemove]);

  const handleCancelRemoveSyncs = useCallback(() => {
    setShowRemoveSyncsConfirm(false);
    setOutlookSyncsToRemove([]);
  }, []);

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const max = useMemo(() => new Date(2025, 4, 10, 23, 59), []);
  const timeslots = 2;
  const views = {
    month: true,
    week: true,
    work_week: true,
    three_day: ThreeDayView,
    day: true,
    list: true
  };

  // Handler for day click in month view - navigates to day view
  const handleDayClick = useCallback((date) => {
    setCurrentDate(date);
    setCurrentView('day');
  }, []);

  const handleSelectSlot = useCallback((slotInfo, e) => {
    // In month view, clicking on a date should navigate to day view
    if (currentView === 'month') {
      handleDayClick(slotInfo.start);
      return;
    }

    // Check if this is a right-click
    if (e && e.type === 'contextmenu') {
      handleRightClickSlot(slotInfo, e);
      return;
    }

    // If modal is already open, don't create a new event
    if (showModal) {
      return;
    }

    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setEditingEvent(null);
    setIsDragging(false);
    setSelectedSlotDate(slotInfo.start);
    setModalPosition(getModalPositionStyle(slotInfo.start, currentView));

    if (eventToCopy) {
      const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
      const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
      const copiedEvent = {
        ...eventToCopy,
        start: slotInfo.start,
        end: newEnd,
      };
      handleSaveEvent(copiedEvent);
      setEventToCopy(null);
    } else {
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start: slotInfo.start,
        end: slotInfo.end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [
        ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
        placeholder
      ]);
      setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
      setShowModal(true);
    }
  }, [eventToCopy, handleSaveEvent, currentView, originalEventBeforeEdit, handleDayClick, handleRightClickSlot, showModal]);

  // The events list is only fetched once per login and never refreshed after
  // that, so a locally-Pending event can have been approved in CRM in the
  // meantime. Re-check fresh whenever an event is opened for editing so the
  // modal locks down (and drag/resize/delete affordances update) without
  // waiting for a page reload.
  const refreshApprovalStatus = useCallback((event) => {
    if (!event?.logId || event.logId.startsWith('temp-') || event.approval_status === 'Marked') {
      return;
    }
    API.fetchTimelogById(event.logId)
      .then((res) => {
        const [fresh] = transformTimelogsToEvents(res?.data ? [res.data] : []);
        if (!fresh || fresh.approval_status !== 'Approved') return;
        setEditingEvent((prev) => (prev?.logId === event.logId ? { ...prev, ...fresh } : prev));
        setEvents((prev) => prev.map((ev) => (ev.logId === event.logId ? { ...ev, ...fresh } : ev)));
      })
      .catch(() => {
        // Best-effort UX check only — the server enforces this on save/delete regardless.
      });
  }, []);

  const handleSelectEvent = useCallback((event) => {
    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setIsDragging(false);
    setShowDeleteConfirm(false);
    setEventPendingDelete(null);
    setOriginalEventBeforeEdit(event);

    // Only set editing event if it's not a marked event
    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    setShowModal(true);
    refreshApprovalStatus(event);
  }, [originalEventBeforeEdit, refreshApprovalStatus]);

  const handleModalTimeChange = useCallback((updatedEvent) => {
    setEvents((prevEvents) =>
      prevEvents.map(ev =>
        // For editing: match by logId, for marked: match by tempId or start/end
        (updatedEvent.logId && ev.logId === updatedEvent.logId) ||
        (!updatedEvent.logId && slotInfo?.tempId && ev.logId === slotInfo.tempId)
          ? { ...ev, start: updatedEvent.start, end: updatedEvent.end }
          : ev
      )
    );
    // Also update editingEvent if present
    setEditingEvent((prev) =>
      prev && prev.logId === updatedEvent.logId
        ? { ...prev, start: updatedEvent.start, end: updatedEvent.end }
        : prev
    );
    // Also update slotInfo for marked events
    if (!updatedEvent.logId && slotInfo) {
      setSlotInfo((prev) => prev ? { ...prev, start: updatedEvent.start, end: updatedEvent.end } : prev);
    }
    
    // 🚀 Auto-scroll calendar to follow the event when moved with arrow keys
    if (updatedEvent.start && (currentView === 'day' || currentView === 'three_day' || currentView === 'week' || currentView === 'work_week')) {
      // Use smooth scrolling for better UX
      scrollToTime(updatedEvent.start, true);
    }
  }, [slotInfo, currentView, scrollToTime]);

  // Memoize calendar sub-components (headers, event renderers, month cell)
  const calendarComponents = useMemo(() => createCalendarComponents({
    currentView,
    formatHours,
    getDailyTotals,
    handleDayClick,
    handleRightClickEvent,
    setupLongPress,
  }), [currentView, getDailyTotals, handleDayClick, handleRightClickEvent, setupLongPress]);
  
  if (error && (error.includes('Error 1') || error.includes('Error 2') || error.includes('Error 3'))) {
    let title, message, details;
    
    if (error.includes('Error 1')) {
      title = 'Authentication Required';
      message = 'Unable to verify your access credentials.';
      details = 'The authentication parameter is missing from your URL.';
    } else if (error.includes('Error 2')) {
      title = 'Login Failed';
      message = 'Authentication could not be completed.';
      details = 'Your login credentials could not be verified with the server.';
    } else if (error.includes('Error 3')) {
      title = 'Connection Error';
      message = 'Unable to connect to the authentication server.';
      details = 'Please check your internet connection and try again.';
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-6">{details}</p>
            <div className="bg-[#d6c08b] rounded-lg p-4">
              <p className="text-sm text-white">
                If the issue persists, please contact the IT department.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <AuthenticatingModal isOpen={true} devLoginEnabled={devLoginEnabled} popupLoginBlocked={popupLoginBlocked} />;
  }

  


  return (
    <div ref={calendarRef} className="h-screen flex flex-col bg-white dark:bg-[#1c1c20] app-container transition-colors duration-200">
      {/* Responsive Navigation */}
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentDate={currentDate}
        handleNavigate={handleNavigate}
        onAddNew={handleAddNew}
        onCreateSeries={handleCreateSeriesFromHeader}
        onOpenWeeklyLog={() => setShowWeeklyLog(true)}
        showOutlookSync={showOutlookSync}
        setShowOutlookSync={setShowOutlookSync}
        onRemoveOutlookSyncs={handleRemoveOutlookSyncs}
        employees={timelogEmployees}
        selectedEmployeeId={selectedTimelogEmployee?.id}
        onEmployeeChange={handleTimelogEmployeeChange}
        employeeLoading={timelogEmployeeLoading}
        canWrite={canWriteTimelogs}
        defaultUseNotes={defaultUseNotes}
        setDefaultUseNotes={(val) => {
          setDefaultUseNotes(val);
          localStorage.setItem('zrm_default_use_notes', val);
        }}
        isDark={isDark}
        toggleTheme={toggleTheme}
      />
      {/* Calendar Container */}
      <div 
        className="flex-1 calendar-container-mobile md:calendar-container-tablet lg:calendar-container-desktop"
        onContextMenu={(e) => {
          // Check if the right-click was on a calendar slot (not on an event)
          const target = e.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate the actual slot time from mouse position
            // Use a much simpler approach - just calculate from pixel position
            const calendarRect = e.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = e.clientY - columnRect.top;
              
              // Get the time gutter to understand the time scale
              const timeView = e.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0; // Default start hour (midnight)
              let endHour = 24; // Default end hour  
              let totalHours = 24; // Default hours shown (full day)
              
              if (timeGutter) {
                // Try to get actual start/end from time labels
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  // Get first time
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  // Get last time  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1; // Add 1 to get end hour
                    }
                  }
                  
                  totalHours = endHour - startHour;
                  console.log(`Calendar time range: ${startHour}:00 - ${endHour}:00 (${totalHours} hours)`);
                }
              }
              
              // Simple pixel-based calculation
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              console.log(`Simple calculation: mouseY=${mouseY}, height=${columnRect.height}, percent=${percentDown.toFixed(3)}, hours=${hoursFromStart.toFixed(2)} → ${finalHour}:${finalMinute.toString().padStart(2, '0')}`);
            
              // Create the slot start time for today (or current date in view)
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
             
              // If we're in day view and it's a different day, use that day
              if (currentView === 'day' || currentView === 'three_day') {
                // Use the date from the day/three_day view
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                // For week views, we need to determine which day column was clicked
                // Find the correct day column selector - react-big-calendar uses different classes
                const timeView = e.currentTarget.querySelector('.rbc-time-view');
                const dayColumns = timeView ? timeView.querySelectorAll('.rbc-time-column') : [];
                let dayIndex = 0;
                
                // Alternative approach: find all day header elements to map positions
                const headers = e.currentTarget.querySelectorAll('.rbc-header');
                
                if (headers.length > 0) {
                  // Use headers to determine day positions
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (e.clientX >= headerRect.left && e.clientX <= headerRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Header ${i} (${headers[i].textContent?.trim()})`);
                      break;
                    }
                  }
                } else if (dayColumns.length > 0) {
                  // Fallback to time columns
                  for (let i = 0; i < dayColumns.length; i++) {
                    const dayRect = dayColumns[i].getBoundingClientRect();
                    if (e.clientX >= dayRect.left && e.clientX <= dayRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Column ${i}`);
                      break;
                    }
                  }
                }
                
                // Calculate the date for this day column
                // Always use isoWeek (Monday start) to match calendar display
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
                
                console.log(`Slot calculated: ${moment(slotStart).format('dddd YYYY-MM-DD HH:mm')}`);
              }
             
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000); // +1 hour
              
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, e);
            }
          }
        }}
        {...setupLongPress((e, syntheticEvent) => {
          // Reuse the same logic as onContextMenu for slots
          const target = syntheticEvent.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate slot time and handle long press like right-click
            const calendarRect = syntheticEvent.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = syntheticEvent.clientY - columnRect.top;
              
              const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;
              
              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1;
                    }
                  }
                  
                  totalHours = endHour - startHour;
                }
              }
              
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
              
              if (currentView === 'day' || currentView === 'three_day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
                const headers = syntheticEvent.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (syntheticEvent.clientX >= headerRect.left && syntheticEvent.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }
              
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, syntheticEvent);
            }
          }
        })}
      >
{currentView === 'list' ? (
          <ListView
            events={filteredEvents}
            currentDate={currentDate}
            onSelectEvent={handleSelectEvent}
            onRightClickEvent={handleRightClickEvent}
            setupLongPress={setupLongPress}
          />
        ) : (
          <DnDCalendar
            events={filteredEvents}
            onDragStart={() => setIsDragging(true)}
            localizer={localizer}
            date={currentDate}
            onNavigate={handleNavigate}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            views={views}
            max={max}
            step={30}
            snapToSlot={false}
            timeslots={timeslots}
            showMultiDayTimes
            selectable={canWriteTimelogs}
            popup
            onSelectSlot={canWriteTimelogs ? handleSelectSlot : undefined}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            formats={FORMATS}
            eventPropGetter={eventPropGetter}
            draggableAccessor={isDraggable}
            resizableAccessor={isResizable}
            components={calendarComponents}
          />
        )}
      </div>

      {/* Responsive Stats Footer */}
      <MobileFooter
        totalsCurrentView={totalsCurrentView}
        totalsYTD={totalsYTD}
        currentView={currentView}
        currentDate={currentDate}
        customRangeData={customRangeData}
        isLoadingCustomRange={isLoadingCustomRange}
        onCustomDateRangeClick={handleCustomDateRangeClick}
        onClearCustomRange={handleClearCustomRange}
      />

      {/* Context Menu for Events */}
      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="absolute bg-white dark:bg-[#242428] border border-gray-200 dark:border-[#36363e] shadow-lg rounded-md p-2 z-50"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          {/* Only show Copy if not Outlook synced event */}
          {canWriteTimelogs && !(contextMenu.event && isOutlookSyncEvent(contextMenu.event)) && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2e2e36] dark:text-gray-200"
              onClick={() => {
                setEventToCopy(contextMenu.event);
                setContextMenu({ visible: false, x: 0, y: 0, event: null });
              }}
            >
              Copy
            </li>
          )}
          {canWriteTimelogs && <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2e2e36] dark:text-gray-200"
            onClick={() => {
              handleCreateSeries(contextMenu.event);
            }}
          >
            Create Series
          </li>}
          {/* Show Delete Entire Series if this event is part of a series */}
          {canWriteTimelogs && contextMenu.event?.seriesId && (
            <li
              className="px-2 py-1 cursor-pointer text-orange-600 hover:bg-gray-100 dark:hover:bg-[#2e2e36]"
              onClick={() => {
                handleDeleteSeries(contextMenu.event.seriesId);
              }}
            >
              Delete Entire Series
            </li>
          )}
          {/* Only show Edit and Delete if not Approved */}
          {canWriteTimelogs && contextMenu.event?.approval_status !== 'Approved' && (
            <>
              <li
                className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2e2e36] dark:text-gray-200"
                onClick={() => {
                  setEditingEvent(contextMenu.event);
                  setShowModal(true);
                  refreshApprovalStatus(contextMenu.event);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                {contextMenu.event && isOutlookSyncEvent(contextMenu.event) ? 'Convert' : 'Edit'}
              </li>
              <li
                className="px-2 py-1 cursor-pointer text-red-500 hover:bg-gray-100 dark:hover:bg-[#2e2e36]"
                onClick={() => {
                  setEventPendingDelete(contextMenu.event);
                  setShowDeleteConfirm(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Delete
              </li>
            </>
          )}
        </ul>
      )}

      {/* Context Menu for Empty Slots */}
      {canWriteTimelogs && slotContextMenu.visible && (
        <ul
          ref={slotContextMenuRef}
          className="absolute bg-white dark:bg-[#242428] border border-gray-200 dark:border-[#36363e] shadow-lg rounded-md p-2 z-50"
          style={{
            top: slotContextMenu.y,
            left: slotContextMenu.x,
          }}
        >
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2e2e36] dark:text-gray-200"
            onClick={() => {
              const slotInfo = slotContextMenu.slot;
              const placeholder = createMarkedPlaceholder(slotInfo.start, slotInfo.end);
              setEvents(prev => [
                // fix: startswith -> startsWith
                ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
                placeholder
              ]);
              setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId: placeholder.logId });
              setModalPosition(getModalPositionStyle(slotInfo.start, currentView));
              setShowModal(true);
              setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
            }}
          >
            Add New
          </li>
          {eventToCopy && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2e2e36] dark:text-gray-200"
              onClick={() => {
                // Paste copied event at this slot
                const slotInfo = slotContextMenu.slot;
                const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
                const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
                const copiedEvent = {
                  ...eventToCopy,
                  start: slotInfo.start,
                  end: newEnd,
                };
                
                handleSaveEvent(copiedEvent);
                setEventToCopy(null);
                setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
              }}
            >
              Paste
            </li>
          )}
        </ul>
      )}

      {!isDragging && (
        <EventFormModal
        defaultUseNotes={defaultUseNotes}
        onTimeChange={handleModalTimeChange}
        onNavigate={handleNavigate}
        currentDate={currentDate}
        key={editingEvent?.logId || slotInfo?.tempId || 'new'}
        modalPosition={modalPosition}
        setModalPosition={setModalPosition}
        getModalPositionStyle={getModalPositionStyle}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingEvent(null);
          if (slotInfo?.tempId) {
            setEvents(prev => prev.filter(ev => ev.logId !== slotInfo.tempId));
          }
          setSlotInfo(null);
          // Only restore if we didn't just save/update
          if (!suppressRevertRef.current && originalEventBeforeEdit) {
            setEvents(prev =>
              prev.map(ev =>
                ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
              )
            );
          }
          setOriginalEventBeforeEdit(null);
          suppressRevertRef.current = false; // reset flag
        }}
        onSave={async (eventData) => {
          // Only for editing, not new
         if (eventData.convertFromCRM) {
          await handleConvertCRMSync(eventData);
        } else if (
          editingEvent &&
          isOutlookSyncEvent(editingEvent) &&
          eventData.projectId !== editingEvent.projectId
        ) {
          // Delete old, then create new
          await handleDeleteEvent(editingEvent.logId);
          handleSaveEvent(eventData);
        } else if (editingEvent) {
          // Subtract the old log's non-billable duration from YTD so the server
          // compares the net change rather than double-counting the existing log.
          const oldNonBillableHours = editingEvent.billingtype !== 'Billable'
            ? (editingEvent.end - editingEvent.start) / 3600000
            : 0;
          handleUpdateEvent({
            ...eventData,
            logId: editingEvent.logId,
            ytdNonBillableHours: totalsYTD.nonBillable - oldNonBillableHours,
          });
        } else {
          handleSaveEvent({ ...eventData, ytdNonBillableHours: totalsYTD.nonBillable });
        }
        setShowModal(false);
        setEditingEvent(null);
        setSlotInfo(null);
        setOriginalEventBeforeEdit(null);
        }}
        onDelete={handleDeleteEvent}
        start={editingEvent?.start || slotInfo?.start}
        end={editingEvent?.end || slotInfo?.end}
        projects={projects}
        initialData={editingEvent}
        selectedSlotDate={selectedSlotDate}
        currentView={currentView}        
        billableAutoFill={billableAutoFill}
        readOnly={!canWriteTimelogs}
      />
      )}
      <ConfirmModal 
        isOpen={showDeleteConfirm}
        onConfirm={() => {
          if (eventPendingDelete) {
            handleDeleteEvent(eventPendingDelete.logId);
            setEventPendingDelete(null);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => {
          setEventPendingDelete(null);
          setShowDeleteConfirm(false);
        }}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      
      {/* Series Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showSeriesDeleteConfirm}
        onConfirm={handleConfirmDeleteSeries}
        onCancel={() => {
          setSeriesPendingDelete({ seriesId: null, events: [] });
          setShowSeriesDeleteConfirm(false);
        }}
        title="Delete Entire Series"
        message={`Are you sure you want to delete ${seriesPendingDelete.events.length} time registration${seriesPendingDelete.events.length !== 1 ? 's' : ''} from this series? This action cannot be undone.`}
        confirmText={`Delete ${seriesPendingDelete.events.length} Registration${seriesPendingDelete.events.length !== 1 ? 's' : ''}`}
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />

      {/* Series Delete Progress Modal */}
      {seriesDeleteProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-[#242428] rounded-lg shadow-xl p-6 w-80">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Deleting Series</h3>
            <p className="text-sm text-gray-500 mb-4">
              Week {seriesDeleteProgress.current} of {seriesDeleteProgress.total}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-300 bg-red-500"
                style={{ width: `${(seriesDeleteProgress.current / seriesDeleteProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}
      
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
      />
      
      {/* Remove Outlook Syncs Confirmation Modal */}
      <ConfirmModal 
        isOpen={showRemoveSyncsConfirm}
        onConfirm={handleConfirmRemoveSyncs}
        onCancel={handleCancelRemoveSyncs}
        title="Remove Outlook Syncs"
        message={`This will delete ${outlookSyncsToRemove.length} Outlook sync event${outlookSyncsToRemove.length !== 1 ? 's' : ''} from the current view. This action cannot be undone. Continue?`}
        confirmText="Remove"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Remove Outlook Syncs Result Modal */}
      {showRemoveSyncsResult && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[2700] flex items-center justify-center p-3 md:p-4">
          <div className="bg-white dark:bg-[#242428] p-3 md:p-6 rounded-lg w-full max-w-[88vw] md:max-w-[400px] shadow-xl">
            <div className="flex justify-end">
              <button
                onClick={() => setShowRemoveSyncsResult(false)}
                className="px-4 py-2.5 md:py-2 text-sm md:text-base bg-[#ccb778] text-white rounded hover:bg-[#b49646] touch-manipulation min-h-[44px] md:min-h-0"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Weekly Time Log Modal */}
      <WeeklyTimeLogModal
        isOpen={showWeeklyLog && canWriteTimelogs}
        onClose={() => setShowWeeklyLog(false)}
        projects={projects}
        isDark={isDark}
        onEntrySaved={(newEvent) => {
          setEvents(prev => [...prev, newEvent]);
          fetchProjects();
        }}
        onEntryDeleted={(logId) => {
          setEvents(prev => prev.filter(ev => ev.logId !== logId));
        }}
      />

      {/* Series Modal */}
      <SeriesModal
        isOpen={showSeriesModal && canWriteTimelogs}
        onClose={() => {
          setShowSeriesModal(false);
          setSeriesTemplateEvent(null);
        }}
        onCreateSeries={handleBatchCreateSeries}
        templateEvent={seriesTemplateEvent}
        projects={projects}
      />

      {/* Custom Date Range Modal */}
      <CustomDateRangeModal
        isOpen={showCustomDateModal}
        onClose={handleCustomDateRangeClose}
        onApply={handleCustomDateRangeApply}
        currentRange={customDateRange}
      />
    </div>
  );
}

App.propTypes = {
  localizer: PropTypes.instanceOf(DateLocalizer),
};

export default App;
